import i18n from '@/libs/i18n'

const { t } = i18n.global

enum Errors {
    InternalServerError = 1,
    InvalidData,
    NotFound,
    Unauthorized,
    UserExist,
    UserDoesNotExist,
    FailedToInsert,
    FileToBig,
    UnableToRetrieveExtension,
    NoAccess,
    IncorrectPassword,
    FieldNameExists,
    CategoryExists,
    ProductBrandExists,
    SalePartnerExists,
    ProductExists,
    ProductSlugExists,
    InUse,
    UnknownType,
    AdditionalPageExists,
    InvalidOrderStatus,
    PriceListForSelectedPartnerIsEmpty,
    ProductTitleRequired,
    ProductCountryRequired,
    ProductVatGroupRequired,
    ProductPriceInvalid,
    ProductDiscountInvalid,
    VatGroupNotFoundForCountry,
    CategoryNotFound,
    SaleWebsiteNotFound,
    ProductBrandNotFound,
    SpecificationNotFound,
    InvalidFieldValue,
    SpecificationNotInCategory,
    SaleWebsiteTypeExists,
    ProductStockInvalid,
    DiscountCouponCodeInvalid,
    DiscountCouponInvalidType,
    DiscountCouponValueInvalid,
    DiscountCouponValidityInvalid,
    DiscountCouponCodeExists,
    DiscountCouponCountryInvalid,
    ProductWarrantyInvalid
}

// Some validation errors (unknown category/sale website/brand name, invalid Excel column) carry
// the offending value in error.data - append it so the user sees exactly what is wrong.
const errorDetail = (error: object): string => {
    if (!('data' in error) || typeof error.data !== 'string' || error.data === '') return ''
    return `: "${error.data}"`
}

export const parseError = (errorObject: unknown, fallback = '') => {
    if (errorObject === null || errorObject === undefined
        || !(errorObject instanceof Object)
        || !('error' in errorObject)
        || !errorObject.error
        || !(errorObject.error instanceof Object)
        || !('code' in errorObject.error)
        || errorObject.error.code === Errors.InternalServerError) {
        if (fallback && fallback !== '') return fallback
        return t('errors.generic_error')
    }

    switch (errorObject.error.code) {
        case Errors.InvalidData:
            return t('errors.invalid_data')
        case Errors.NotFound:
            return t('errors.not_found')
        case Errors.Unauthorized:
            return t('errors.unauthorized')
        case Errors.UserExist:
            return t('errors.user_exist')
        case Errors.UserDoesNotExist:
            return t('errors.user_does_not_exist')
        case Errors.FailedToInsert:
            return t('errors.failed_to_insert')
        case Errors.FileToBig:
            return t('errors.file_to_big')
        case Errors.UnableToRetrieveExtension:
            return t('errors.unable_to_retrieve_extension')
        case Errors.NoAccess:
            return t('errors.no_access')
        case Errors.IncorrectPassword:
            return t('errors.incorrect_password')
        case Errors.FieldNameExists:
            return t('errors.field_name_exists')
        case Errors.CategoryExists:
            return t('errors.category_exists')
        case Errors.ProductBrandExists:
            return t('errors.product_brand_exists')
        case Errors.SalePartnerExists:
            return t('errors.sale_partner_exists')
        case Errors.ProductExists:
            return t('errors.product_exists')
        case Errors.ProductSlugExists:
            return t('errors.product_slug_exists')
        case Errors.UnknownType:
            return t('errors.unknown_type')
        case Errors.AdditionalPageExists:
            return t('errors.additional_page_exists')
        case Errors.InvalidOrderStatus:
            return t('errors.invalid_order_status')
        case Errors.PriceListForSelectedPartnerIsEmpty:
            return t('errors.price_list_for_selected_partner_is_empty')
        case Errors.ProductTitleRequired:
            return t('errors.product_title_required')
        case Errors.ProductCountryRequired:
            return t('errors.product_country_required')
        case Errors.ProductVatGroupRequired:
            return t('errors.product_vat_group_required')
        case Errors.ProductPriceInvalid:
            return t('errors.product_price_invalid')
        case Errors.ProductDiscountInvalid:
            return t('errors.product_discount_invalid')
        case Errors.ProductStockInvalid:
            return t('errors.product_stock_invalid')
        case Errors.VatGroupNotFoundForCountry:
            return t('errors.vat_group_not_found_for_country') + errorDetail(errorObject.error)
        case Errors.CategoryNotFound:
            return t('errors.category_not_found') + errorDetail(errorObject.error)
        case Errors.SaleWebsiteNotFound:
            return t('errors.sale_website_not_found') + errorDetail(errorObject.error)
        case Errors.ProductBrandNotFound:
            return t('errors.product_brand_not_found') + errorDetail(errorObject.error)
        case Errors.SpecificationNotFound:
            return t('errors.specification_not_found')
        case Errors.InvalidFieldValue:
            return t('errors.invalid_field_value') + errorDetail(errorObject.error)
        case Errors.SaleWebsiteTypeExists:
            return t('errors.sale_website_type_exists')
        case Errors.DiscountCouponCodeInvalid:
            return t('errors.discount_coupon_code_invalid')
        case Errors.DiscountCouponInvalidType:
            return t('errors.discount_coupon_invalid_type')
        case Errors.DiscountCouponValueInvalid:
            return t('errors.discount_coupon_value_invalid')
        case Errors.DiscountCouponValidityInvalid:
            return t('errors.discount_coupon_validity_invalid')
        case Errors.DiscountCouponCodeExists:
            return t('errors.discount_coupon_code_exists')
        case Errors.DiscountCouponCountryInvalid:
            return t('errors.discount_coupon_country_invalid')
        case Errors.ProductWarrantyInvalid:
            return t('errors.product_warranty_invalid')
        default:
            return t('errors.generic_error')
    }
}

export const isNotFoundError = (errorObject: unknown): boolean => {
    if (errorObject === null || errorObject === undefined
        || !(errorObject instanceof Object)
        || !('error' in errorObject)
        || !errorObject.error
        || !(errorObject.error instanceof Object)
        || !('code' in errorObject.error)) {
        return false
    }

    return errorObject.error.code === Errors.NotFound
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const isInUseError = <T = unknown>(errorObject: unknown): { isInUse: boolean, item: null | T } => {
    if (errorObject === null || errorObject === undefined
        || !(errorObject instanceof Object)
        || !('error' in errorObject)
        || !errorObject.error
        || !(errorObject.error instanceof Object)
        || !('code' in errorObject.error)
        || !('data' in errorObject.error)
        || errorObject.error.code !== Errors.InUse) {
        return { isInUse: false, item: null }
    }

    return { isInUse: true, item: errorObject.error.data as T }
}
